/* eslint-disable no-console */
import i18n from 'i18next'
import {
  add,
  dissoc,
  findIndex,
  prepend,
  propEq,
  reject,
  subtract,
  update,
} from 'ramda'
import { toast } from 'react-toastify'

import { State } from './store.types'
import { Actions, Types } from './storeReducer.types'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[StoreReducer]'

const getToastText = (status: string) => {
  switch (status) {
    case 'PREPARING_UPLOAD':
      return i18n.t('Preparing images')
    case 'SAVING_IMAGES':
      return i18n.t('Saving images')
    case 'ANALYZING_IMAGES':
      return i18n.t('Analyzing images')
    case 'PERSISTING_DATA':
      return i18n.t('Persisting images')
    case 'UPDATING_UI':
      return i18n.t('Updating images')
    default:
      return ''
  }
}

const storeReducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case Types.FetchProjects:
      return {
        ...state,
        projects: action.payload,
      }

    case Types.FetchMoreProjects:
      return {
        ...state,
        projects: {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
          results: [
            ...(state.projects ? state.projects.results : []),
            ...action.payload.results,
          ],
        },
      }

    case Types.EmptyProjects:
      return dissoc('projects', state)

    case Types.FetchProject:
      return {
        ...state,
        project: action.payload,
      }

    case Types.CreateProject:
      return {
        ...state,
        projects: state.projects && {
          ...state.projects,
          count: add(1, state.projects.count),
          results: prepend(action.payload, state.projects.results),
        },
      }

    case Types.UpdateProject:
      return {
        ...state,
        projects: state.projects && {
          ...state.projects,
          results: update(
            findIndex(propEq('id', action.payload.id), state.projects.results),
            action.payload,
            state.projects.results
          ),
        },
      }

    case Types.DeleteProject:
      return {
        ...state,
        projects: state.projects && {
          ...state.projects,
          count: subtract(state.projects.count, 1),
          results: reject(
            (a) => a.id === action.payload,
            state.projects.results
          ),
        },
      }

    case Types.FetchProducts:
      return {
        ...state,
        products: action.payload,
      }

    case Types.FetchMoreProducts:
      return {
        ...state,
        products: {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
          results: [
            ...(state.products ? state.products.results : []),
            ...action.payload.results,
          ],
        },
      }

    case Types.EmptyProducts:
      return dissoc('products', state)

    case Types.SetProduct:
      return {
        ...state,
        product: action.payload,
      }

    case Types.CreateProduct:
      return {
        ...state,
        products: state.products && {
          ...state.products,
          count: add(1, state.products.count),
          results: prepend(action.payload, state.products.results),
        },
      }

    case Types.UpdateProduct:
      return {
        ...state,
        products: state.products && {
          ...state.products,
          results: update(
            findIndex(propEq('id', action.payload.id), state.products.results),
            action.payload,
            state.products.results
          ),
        },
      }

    case Types.DeleteProduct:
      return {
        ...state,
        products: state.products && {
          ...state.products,
          count: subtract(state.products.count, 1),
          results: reject(
            (a) => a.id === action.payload,
            state.products.results
          ),
        },
      }

    case Types.FetchProductClasses:
      return {
        ...state,
        productClasses: action.payload,
      }

    case Types.EmptyProductClasses:
      return dissoc('productClasses', state)

    case Types.FetchTrainings:
      return {
        ...state,
        trainings: action.payload,
      }

    case Types.FetchMoreTrainings:
      return {
        ...state,
        trainings: {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
          results: [
            ...(state.trainings ? state.trainings.results : []),
            ...action.payload.results,
          ],
        },
      }

    case Types.EmptyTrainings:
      return dissoc('trainings', state)

    case Types.SetTraining:
      return {
        ...state,
        training: action.payload,
      }

    case Types.DeleteTraining:
      return {
        ...state,
        trainings: state.trainings && {
          ...state.trainings,
          count: subtract(state.trainings.count, 1),
          results: reject(
            (a) => a.id === action.payload,
            state.trainings.results
          ),
        },
      }

    case Types.UpdateTrainingStatus:
      return {
        ...state,
        trainings: state.trainings && {
          ...state.trainings,
          results: state.trainings.results.map((t) =>
            t.id === action.payload.trainingId
              ? {
                  ...t,
                  ...action.payload,
                }
              : t
          ),
        },
      }

    case Types.FetchTrainingTests:
      return {
        ...state,
        trainingTests: action.payload,
      }

    case Types.FetchMoreTrainingTests:
      return {
        ...state,
        trainingTests: {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
          results: [
            ...(state.trainingTests ? state.trainingTests.results : []),
            ...action.payload.results,
          ],
        },
      }

    case Types.EmptyTrainingTests:
      return dissoc('trainingTests', state)

    case Types.SetTrainingTest:
      return {
        ...state,
        trainingTest: action.payload,
      }

    case Types.DeleteTrainingTest:
      return {
        ...state,
        trainingTests: state.trainingTests && {
          ...state.trainingTests,
          count: subtract(state.trainingTests.count, 1),
          results: reject(
            (a) => a.id === action.payload,
            state.trainingTests.results
          ),
        },
      }

    case Types.UpdateTrainingTestStatus:
      return {
        ...state,
        trainingTests: state.trainingTests && {
          ...state.trainingTests,
          results: state.trainingTests.results.map((t) =>
            t.id === action.payload.trainingTestId
              ? {
                  ...t,
                  ...action.payload,
                }
              : t
          ),
        },
      }

    case Types.AlertNotificationImageUpload: {
      const { uploadId } = action.payload
      const { status } = action.payload
      let alertNotifications = state.alertNotifications || []

      console.log(LOG_PREFIX, 'AlertNotificationImageUpload received', {
        uploadId,
        status,
        progress: action.payload.progress,
        observationsCount: action.payload.observationsId?.length,
        currentNotificationsCount: alertNotifications.length,
        timestamp: new Date().toISOString(),
        existingStatuses: alertNotifications.map((an) => ({
          uploadId: an.uploadId,
          status: an.status,
        })),
      })

      let toastId = alertNotifications?.find((an) => an.uploadId === uploadId)
        ?.toastId

      if (toastId === undefined) {
        if (status !== 'PREPARING_UPLOAD') {
          console.warn(
            LOG_PREFIX,
            'No toastId found and status is not PREPARING_UPLOAD, ignoring',
            {
              uploadId,
              status,
            }
          )
          return state
        }

        // Create a new toast
        toastId = toast('', {
          progress: 0,
          autoClose: false,
          type: toast.TYPE.DEFAULT,
          closeButton: true,
          closeOnClick: false,
          isLoading: true,
        }).toString()

        console.log(LOG_PREFIX, 'Created new toast for upload', {
          uploadId,
          toastId,
        })

        // Add the new toast reference to the state
        alertNotifications.push({
          uploadId,
          toastId,
        })
      }

      // Get the previous status for this upload to detect phase transitions
      const previousStatus = alertNotifications.find(
        (an) => an.uploadId === uploadId
      )?.status

      switch (status) {
        case 'ERROR':
          console.error(LOG_PREFIX, 'Upload ERROR - dismissing toast', {
            uploadId,
            toastId,
            previousStatus,
            timestamp: new Date().toISOString(),
          })
          // Remove the toast from the state and show an error message
          toast.dismiss(toastId)
          alertNotifications.splice(
            alertNotifications.findIndex((an) => an.toastId === toastId),
            1
          )
          toast.error(i18n.t('An error occurred while uploading the image'))
          break
        case 'PREPARING_UPLOAD':
        case 'SAVING_IMAGES':
        case 'ANALYZING_IMAGES':
        case 'PERSISTING_DATA':
        case 'UPDATING_UI': {
          // Log phase transition (when status changes)
          const isPhaseTransition = previousStatus !== status
          if (isPhaseTransition) {
            if (previousStatus) {
              const endedPhaseText =
                getToastText(previousStatus) || previousStatus
              console.log(LOG_PREFIX, `>>> PHASE END: ${endedPhaseText}`, {
                uploadId,
                toastId,
                endedPhase: previousStatus,
                timestamp: new Date().toISOString(),
              })
            }
            const startedPhaseText = getToastText(status) || status
            console.log(LOG_PREFIX, `>>> PHASE START: ${startedPhaseText}`, {
              uploadId,
              toastId,
              startedPhase: status,
              previousPhase: previousStatus || 'none',
              timestamp: new Date().toISOString(),
            })
          }

          console.log(LOG_PREFIX, 'Updating toast status', {
            uploadId,
            toastId,
            status,
            previousStatus,
            isPhaseTransition,
            progress: action.payload.progress,
            toastText: getToastText(status),
            timestamp: new Date().toISOString(),
          })
          toast.update(toastId, {
            render: getToastText(status),
            progress:
              action.payload.progress && action.payload.progress >= 1.0
                ? 0.999
                : action.payload.progress,
          })
          alertNotifications = alertNotifications.map((an) =>
            an.toastId === toastId && an.status !== status
              ? { ...an, status }
              : an
          )
          break
        }
        case 'COMPLETED': {
          // BE completed
          // Log end of previous phase if there was one
          if (previousStatus && previousStatus !== 'COMPLETED') {
            const endedPhaseText =
              getToastText(previousStatus) || previousStatus
            console.log(LOG_PREFIX, `>>> PHASE END: ${endedPhaseText}`, {
              uploadId,
              toastId,
              endedPhase: previousStatus,
              reason: 'Backend signaled COMPLETED',
              timestamp: new Date().toISOString(),
            })
          }
          console.log(LOG_PREFIX, 'Backend COMPLETED - waiting for UI update', {
            uploadId,
            toastId,
            previousStatus,
            observationsCount: action.payload.observationsId?.length,
            timestamp: new Date().toISOString(),
          })
          alertNotifications = alertNotifications.map((an) =>
            an.toastId === toastId
              ? {
                  ...an,
                  observationsId: action.payload.observationsId,
                  status,
                }
              : an
          )
          break
        }
        case 'COMPLETED_UPDATING_UI': {
          // FE completed
          // Log end of UPDATING_UI phase
          if (previousStatus === 'UPDATING_UI') {
            console.log(LOG_PREFIX, '>>> PHASE END: Updating images', {
              uploadId,
              toastId,
              endedPhase: 'UPDATING_UI',
              reason: 'Frontend signaled COMPLETED_UPDATING_UI',
              timestamp: new Date().toISOString(),
            })
          }
          console.log(
            LOG_PREFIX,
            'Frontend COMPLETED_UPDATING_UI - closing toast',
            {
              uploadId,
              toastId,
            }
          )
          toast.update(toastId, {
            render: i18n.t('Images uploaded successfully'),
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeButton: true,
            closeOnClick: true,
          })
          alertNotifications.splice(
            alertNotifications.findIndex((an) => an.toastId === toastId),
            1
          )
          toast.done(toastId)
          break
        }

        default:
          console.warn(LOG_PREFIX, 'Unknown status received', {
            uploadId,
            status,
          })
          break
      }

      console.log(LOG_PREFIX, 'AlertNotification processed', {
        uploadId,
        status,
        remainingNotifications: alertNotifications.length,
        notificationIds: alertNotifications.map((an) => an.uploadId),
        finalStatuses: alertNotifications.map((an) => ({
          uploadId: an.uploadId,
          status: an.status,
          hasObservationsId: !!an.observationsId,
        })),
        timestamp: new Date().toISOString(),
      })

      return {
        ...state,
        alertNotifications,
      }
    }

    default:
      return state
  }
}

export default storeReducer
